import { useState } from "react";
import { Alert, Avatar, Box, CircularProgress, Link, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { Tarjeta } from "../../components/kit/Tarjeta";
import { useDocentesPublicos, useSuperAreas } from "../../queries/useDocentes";
import { tokens } from "../../theme";
import { DocentePublico } from "../../types/docente";
import { useIr } from "../../utils/enlaces";

/**
 * "Docentes": con quién se puede trabajar.
 *
 * El catálogo de temas responde "qué hay disponible"; esta vista responde la
 * pregunta de antes, la que se hace quien todavía no encuentra tema: quién
 * trabaja en lo que me interesa y cómo lo contacto. Por eso el correo se muestra
 * y no se esconde: es la acción de la página.
 *
 * Se listan todos y el filtro es por **supercategoría**, no por área: las áreas
 * finas son casi sesenta y como lista desplegable no se leen. Las áreas de cada
 * profesor están en su ficha, que es donde hay espacio para mostrarlas.
 *
 * Los tres últimos temas están para leer a qué se dedica, no para postular: da
 * igual si están tomados, y por eso no llevan su estado. Para eso está el catálogo.
 */
export default function Docentes() {
  const [q, setQ] = useState("");
  const [superarea, setSuperarea] = useState("");
  const { data, isLoading, isError } = useDocentesPublicos(q, superarea);
  const { data: superareas = [] } = useSuperAreas();

  const docentes = data ?? [];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 0.5 }}>
        Docentes
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Profesores que ofrecen temas de memoria. Filtra por área para ver quién trabaja en lo que te interesa y escríbele.
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2.5 }}>
        <TextField
          size="small"
          label="Buscar"
          placeholder="Nombre o apellido…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ minWidth: 240 }}
        />
        <TextField
          select
          size="small"
          label="Área"
          value={superarea}
          onChange={(e) => setSuperarea(e.target.value)}
          sx={{ minWidth: 280 }}
        >
          <MenuItem value="">Todas las áreas</MenuItem>
          {superareas.map((s) => (
            <MenuItem key={s.id} value={String(s.id)}>
              {s.nombre}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}
      {isError && <Alert severity="error">No se pudo cargar la lista de docentes.</Alert>}

      {!isLoading && !isError && docentes.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
          {superarea || q ? "Ningún profesor calza con ese filtro." : "Todavía no hay profesores publicando temas."}
        </Typography>
      )}

      {!isLoading && !isError && docentes.length > 0 && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
            {docentes.length} {docentes.length === 1 ? "profesor" : "profesores"}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" },
            }}
          >
            {docentes.map((d) => (
              <TarjetaDocente key={d.rut} docente={d} />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}

/**
 * Tarjeta de un profesor: la foto grande y centrada, y bajo ella su nombre y su
 * correo. La tarjeta entera lleva a su ficha; el correo no, que es un `mailto`.
 */
function TarjetaDocente({ docente }: { docente: DocentePublico }) {
  const ir = useIr();

  return (
    <Tarjeta onClick={(e) => ir(e, `/app/docentes/${docente.rut}`)}>
      <Stack alignItems="center" spacing={1} sx={{ textAlign: "center" }}>
        {/* Sin ampliar al pasar el mouse: acá la foto ya es grande, y ochenta
            avatares que crecen al rozarlos hacen que recorrer la grilla salte.
            En la ficha del profesor sí se amplía, que es donde se mira una. */}
        <Avatar src={docente.foto ?? undefined} sx={{ width: 96, height: 96, fontSize: 42 }}>
          {docente.nombre.charAt(0)}
        </Avatar>
        <Box sx={{ minWidth: 0, width: "100%" }}>
          <Typography variant="subtitle1" sx={{ lineHeight: 1.3 }}>
            {docente.nombre}
          </Typography>
          {docente.email ? (
            <Link
              href={`mailto:${docente.email}`}
              variant="body2"
              underline="hover"
              // El correo es su propia acción: el clic no debe abrir la ficha.
              onClick={(e) => e.stopPropagation()}
              sx={{ color: tokens.marca.texto, wordBreak: "break-all" }}
            >
              {docente.email}
            </Link>
          ) : (
            <Typography variant="body2" color="text.disabled">
              Sin correo cargado
            </Typography>
          )}
        </Box>
      </Stack>

      <Box sx={{ mt: 2 }}>
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.5, lineHeight: 1.4 }}>
          Últimos temas
        </Typography>
        {docente.temas.length ? (
          // Lista de verdad (`ul`/`li`) y no viñetas dibujadas a mano: son tres
          // ítems sueltos y así los anuncia también un lector de pantalla.
          <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2.5, listStyleType: "disc" }}>
            {docente.temas.map((t) => (
              <Box component="li" key={t.id} sx={{ mb: 0.5, "&:last-of-type": { mb: 0 }, "&::marker": { color: "text.disabled" } }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  underline="hover"
                  title={t.titulo}
                  onClick={(e) => {
                    e.stopPropagation();
                    ir(e, `/app/temas/${t.id}`);
                  }}
                  onAuxClick={(e) => {
                    e.stopPropagation();
                    ir(e, `/app/temas/${t.id}`);
                  }}
                  sx={{
                    color: tokens.marca.texto,
                    textAlign: "left",
                    display: "block",
                    width: "100%",
                    // Una línea por tema: los títulos largos harían tarjetas de
                    // alturas distintas y la grilla dejaría de leerse como grilla.
                    // El título entero queda en el tooltip.
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t.titulo}
                </Link>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 0.5 }}>
            Todavía no ha publicado temas
          </Typography>
        )}
      </Box>
    </Tarjeta>
  );
}
